export type IncidentStatus =
  | 'Archived'
  | 'Open'
  | 'New'
  | 'Under Review'
  | 'Reviewed - Unaccounted'
  | 'Reviewed - Accounted'
  | 'Pending Response'
  | 'Practice Issue'
  | 'Investigation Resolved'
  | 'Unresolved';

/**
 * Incident Status Icons Configuration
 * Maps incident status values to their corresponding image paths
 */
export const INCIDENT_STATUS_ICONS: Record<IncidentStatus, string> = {
  'Archived': '/archived.svg',
  'Investigation Resolved': 'pi pi-file-check',
  'New': '/variance-status/New%20filled.svg',
  'Open': '/variance-status/variance-open.svg',
  'Pending Response': '/variance-status/incident-pending-response.svg',
  'Practice Issue': '/variance-status/incident-practice-issue.svg',
  'Reviewed - Accounted': '/variance-status/incident-reviewed-accounted.svg',
  'Reviewed - Unaccounted': '/variance-status/incident-reviewed-unaccounted.svg',
  'Under Review': '/variance-status/incident-under-review.svg',
  'Unresolved': '/variance-status/incident-unresolved.svg',
} as const;

export const INCIDENT_STATUS_ICON_COLORS: Record<IncidentStatus, string> = {
  'Archived': 'text-red-400',
  'Investigation Resolved': 'text-lime-400',
  'New': 'text-white',
  'Open': 'text-white',
  'Pending Response': 'text-amber-400',
  'Practice Issue': 'text-lime-400',
  'Reviewed - Accounted': 'text-lime-400',
  'Reviewed - Unaccounted': 'text-red-400',
  'Under Review': 'text-amber-400',
  'Unresolved': 'text-red-500',
} as const;

export function getIncidentStatusIconColor(status: IncidentStatus): string {
  const key = (typeof status === 'string' ? status.trim() : '') as IncidentStatus;
  return INCIDENT_STATUS_ICON_COLORS[key] ?? 'text-white';
}

export const INCIDENT_STATUS_OPTIONS: readonly { label: string; value: IncidentStatus }[] = [
  {label: 'Archived', value: 'Archived'},
  { label: 'Investigation Resolved', value: 'Investigation Resolved' },
  { label: 'New', value: 'New' },
  { label: 'Open', value: 'Open' },
  { label: 'Pending Response', value: 'Pending Response' },
  { label: 'Practice Issue', value: 'Practice Issue' },
  { label: 'Reviewed - Accounted', value: 'Reviewed - Accounted' },
  { label: 'Reviewed - Unaccounted', value: 'Reviewed - Unaccounted' },
  { label: 'Under Review', value: 'Under Review' },
  { label: 'Unresolved', value: 'Unresolved' },
] as const;

export function getIncidentStatusIcon(status: IncidentStatus): string {
  const key = (typeof status === 'string' ? status.trim() : '') as IncidentStatus;
  if (key.toLowerCase() === 'unresolved') {
    return '/variance-status/incident-unresolved.svg';
  }
  return INCIDENT_STATUS_ICONS[key] ?? '/variance-status/variance-open.svg';
}

/** Public URL path (leading slash) for email / fetch; maps PrimeNG-only statuses to an SVG asset. */
export function getIncidentStatusEmailAssetPath(status: string): string {
  const key = (typeof status === 'string' ? status.trim() : '') as IncidentStatus;
  if (key.toLowerCase() === 'unresolved') {
    return '/variance-status/incident-unresolved.svg';
  }
  const icon = INCIDENT_STATUS_ICONS[key];
  if (!icon) {
    return '/variance-status/variance-open.svg';
  }
  if (icon.startsWith('pi ')) {
    return '/variance-status/incident-investigator-resolved.svg';
  }
  return icon.startsWith('/') ? icon : `/${icon}`;
}
